import { PropsWithChildren, ReactElement } from "react";
import { Box, Link, Stack, StackProps, SxProps } from "@mui/material";
import { Theme } from "@mui/system";
import { Typography } from "src/components-mui/base/typography/typography";
import { toSxArray } from "src/utils/mui/sx/sx";

const TABLE_ENTITY_TITLE_LINK_TEST_ID = "table-entity-cell-title-link";
const TABLE_ENTITY_NAME_LINK_TEST_ID = "table-entity-cell-name-link";

interface ITableEntityProps extends StackProps {
    title: string;
    titleUrl?: string;
    name?: string;
    nameUrl?: string;
    avatarUrl?: string;
    openLinksInNewTab?: boolean;
    AvatarComponent: ReactElement;
}

interface ILinkWrapper extends PropsWithChildren {
    href?: string;
    openInNewTab?: boolean;
    sx?: SxProps<Theme>;
}

const NEW_TAB_LINK_PROPS = { target: "_blank" } as const;

const LinkWrapper = ({ href, openInNewTab = false, children, sx, ...props }: ILinkWrapper) => {
    const baseSx = {
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",
        ...sx,
    };
    const newTabLinkProps = openInNewTab ? NEW_TAB_LINK_PROPS : {};

    return href ? (
        <Link href={href} underline="hover" sx={baseSx} {...newTabLinkProps} {...props}>
            {children}
        </Link>
    ) : (
        <Typography
            component="span"
            {...props}
            sx={[
                {
                    fontSize: "sm",
                    fontWeight: "bold",
                },
                ...toSxArray(baseSx),
            ]}
        >
            {children}
        </Typography>
    );
};

export const TableEntity = ({
    AvatarComponent,
    title,
    titleUrl,
    name,
    nameUrl,
    avatarUrl,
    openLinksInNewTab = false,
    ...props
}: ITableEntityProps) => {
    return (
        <Stack
            direction="row"
            {...props}
            sx={[
                {
                    gap: 1,
                    overflow: "hidden",
                    order: 0,
                    alignItems: "center",
                },
                ...toSxArray(props.sx),
            ]}
        >
            {avatarUrl ? (
                <Link href={avatarUrl} {...(openLinksInNewTab ? NEW_TAB_LINK_PROPS : {})}>
                    {AvatarComponent}
                </Link>
            ) : (
                AvatarComponent
            )}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 0,
                    overflow: "hidden",
                }}
            >
                <LinkWrapper href={titleUrl} openInNewTab={openLinksInNewTab} data-test-id={TABLE_ENTITY_TITLE_LINK_TEST_ID}>
                    {title}
                </LinkWrapper>
                <LinkWrapper href={nameUrl} openInNewTab={openLinksInNewTab} data-test-id={TABLE_ENTITY_NAME_LINK_TEST_ID}>
                    <Typography
                        component="span"
                        sx={{
                            fontWeight: "normal",
                            fontSize: "inherit",
                        }}
                    >
                        {name}
                    </Typography>
                </LinkWrapper>
            </Box>
        </Stack>
    );
};
